import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FiPlus, FiEdit2, FiSend, FiSearch, FiClock, FiAlertTriangle } from 'react-icons/fi';
import { datosAPI, catalogosAPI, flujoAPI } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import ModalEnviarFlujo from '../components/ModalEnviarFlujo';
import HistoricidadRoleSelector from '../components/HistoricidadRoleSelector';

const BandejaGestor = () => {
  const [datos, setDatos] = useState([]);
  const [catalogos, setCatalogos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPrioridad, setSelectedPrioridad] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [datoSeleccionado, setDatoSeleccionado] = useState(null);
  const [seleccionados, setSeleccionados] = useState([]);
  const [modoMasivo, setModoMasivo] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);
  const { hasRole, user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!hasRole('gestor')) {
      navigate('/');
      return;
    }
    cargarCatalogos();
  }, []);

  // Cargar datos solo cuando se selecciona un rol
  useEffect(() => {
    if (selectedRole === 'gestor') {
      cargarDatos();
    }
  }, [selectedRole]);

  const handleRoleChange = (role) => {
    setSelectedRole(role);
    
    // Si el rol seleccionado no es gestor, redirigir a la bandeja correspondiente
    if (role === 'validador') {
      navigate('/historicidad/validador');
    } else if (role === 'editor') {
      navigate('/historicidad/editor');
    } else if (role === 'historiador') {
      navigate('/historicidad/gestor/nuevo');
    }
  };

  const cargarCatalogos = async () => {
    try {
      const response = await catalogosAPI.listar();
      setCatalogos(response.data.data);
    } catch (error) {
      console.error('Error al cargar catálogos:', error);
    }
  };

  const cargarDatos = async () => {
    try {
      setLoading(true);
      const params = {
        search: searchTerm,
        prioridad: selectedPrioridad
      };
      const response = await datosAPI.getBandeja(params);
      setDatos(response.data.data);
    } catch (error) {
      console.error('Error al cargar bandeja:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      if (!loading) cargarDatos();
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm, selectedPrioridad]);

  const toggleSeleccion = (datoId) => {
    setSeleccionados(prev => 
      prev.includes(datoId) 
        ? prev.filter(id => id !== datoId)
        : [...prev, datoId]
    );
  };

  const toggleTodos = () => {
    // Solo seleccionar los que tienen estado 'creada_gestor' (pueden ser enviados)
    const datosEnviables = datos.filter(d => d.estado === 'creada_gestor');
    if (seleccionados.length === datosEnviables.length && datosEnviables.length > 0) {
      setSeleccionados([]);
    } else {
      setSeleccionados(datosEnviables.map(d => d.id));
    }
  };

  const abrirModalEnvio = (dato) => {
    setDatoSeleccionado(dato);
    setModoMasivo(false);
    setModalOpen(true);
  };

  const abrirModalEnvioMasivo = () => {
    if (seleccionados.length === 0) {
      alert('Selecciona al menos un registro');
      return;
    }
    setDatoSeleccionado(null);
    setModoMasivo(true);
    setModalOpen(true);
  };

  const cerrarModal = () => {
    setModalOpen(false);
    setDatoSeleccionado(null);
    setModoMasivo(false);
  };

  const confirmarEnvio = async (motivo) => {
    try {
      if (modoMasivo) {
        // Procesar acciones masivas
        let exitosos = 0;
        let fallidos = 0;

        for (const datoId of seleccionados) {
          try {
            await flujoAPI.transicionar(
              datoId,
              'informacion_valida',
              motivo
            );
            exitosos++;
          } catch (error) {
            console.error(`Error al procesar dato ${datoId}:`, error);
            fallidos++;
          }
        }

        alert(`Acción masiva completada:\n✅ Exitosos: ${exitosos}\n❌ Fallidos: ${fallidos}`);
        setSeleccionados([]);
      } else {
        // Acción individual
        await flujoAPI.transicionar(
          datoSeleccionado.id,
          'informacion_valida',
          motivo
        );
      }

      cerrarModal();
      cargarDatos();
    } catch (error) {
      console.error('Error al enviar a validación:', error);
      throw error;
    }
  };

  const getPrioridadColor = (prioridad) => {
    switch (prioridad) {
      case 'alta': return 'bg-red-100 text-red-800';
      case 'media': return 'bg-yellow-100 text-yellow-800';
      case 'baja': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getEstadoLabel = (estado) => {
    const labels = {
      'creada_gestor': 'En Trabajo',
      'pendiente': 'Borrador'
    };
    return labels[estado] || estado;
  };

  const getEstadoColor = (estado) => {
    const colors = {
      'creada_gestor': 'bg-blue-100 text-blue-800',
      'pendiente': 'bg-gray-100 text-gray-800'
    };
    return colors[estado] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-cyan-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <h1 className="text-3xl font-bold mb-2">Bandeja del Gestor</h1>
          <p className="text-blue-100">
            Gestiona tus investigaciones históricas
          </p>
          <div className="mt-4">
            <Link
              to="/historicidad/gestor/nuevo"
              className="inline-flex items-center px-4 py-2 bg-white text-blue-600 rounded-lg hover:bg-blue-50 transition font-semibold"
            >
              <FiPlus className="mr-2" />
              Nueva Investigación
            </Link>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Selector de Rol */}
        <HistoricidadRoleSelector
          selectedRole={selectedRole}
          onRoleChange={handleRoleChange}
        />

        {/* Solo mostrar contenido si hay un rol seleccionado y es 'gestor' */}
        {!selectedRole && (
          <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-6">
            <div className="flex">
              <div className="flex-shrink-0">
                <FiAlertTriangle className="h-5 w-5 text-yellow-400" />
              </div>
              <div className="ml-3">
                <p className="text-sm text-yellow-700">
                  Por favor, selecciona un rol para continuar operando en este módulo.
                </p>
              </div>
            </div>
          </div>
        )}

        {selectedRole === 'gestor' && (
          <>
            {/* Filtros */}
            <div className="bg-white rounded-lg shadow p-4 mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                <input
                  type="text"
                  placeholder="Buscar por tema o palabras clave..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
            <div className="md:w-48">
              <select
                value={selectedPrioridad}
                onChange={(e) => setSelectedPrioridad(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Todas las prioridades</option>
                <option value="alta">Alta</option>
                <option value="media">Media</option>
                <option value="baja">Baja</option>
              </select>
            </div>
          </div>
        </div>

        {/* Lista de datos */}
        {datos.length === 0 ? (
          <div className="bg-white rounded-lg shadow p-12 text-center">
            <FiClock className="mx-auto text-gray-400 mb-4" size={48} />
            <p className="text-gray-500 text-lg mb-4">No tienes datos en tu bandeja</p>
            <Link
              to="/historicidad/gestor/nuevo"
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              <FiPlus className="mr-2" />
              Crear primera investigación
            </Link>
          </div>
        ) : (
          <>
            {/* Acciones masivas */}
            <div className="bg-white rounded-lg shadow p-4 mb-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={seleccionados.length === datos.filter(d => d.estado === 'creada_gestor').length && datos.filter(d => d.estado === 'creada_gestor').length > 0}
                      onChange={toggleTodos}
                      className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500"
                    />
                    <span className="ml-2 text-sm font-medium text-gray-700">
                      Seleccionar todos enviables ({seleccionados.length}/{datos.filter(d => d.estado === 'creada_gestor').length})
                    </span>
                  </label>
                </div>
                {seleccionados.length > 0 && (
                  <button
                    onClick={abrirModalEnvioMasivo}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition text-sm font-medium"
                  >
                    <FiSend className="inline mr-1" size={14} />
                    Enviar {seleccionados.length} registros
                  </button>
                )}
              </div>
            </div>

            <div className="bg-white rounded-lg shadow overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 w-12">
                      {/* Checkbox header */}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Tema
                    </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Estado
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Prioridad
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Catálogos
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Tiempo (hrs)
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {datos.map((dato) => (
                  <tr key={dato.id} className={`hover:bg-gray-50 ${dato.fueDevuelto ? 'bg-red-50' : ''}`}>
                    <td className="px-4 py-4">
                      {dato.estado === 'creada_gestor' && (
                        <input
                          type="checkbox"
                          checked={seleccionados.includes(dato.id)}
                          onChange={() => toggleSeleccion(dato.id)}
                          className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                        />
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-start gap-2">
                        {dato.fueDevuelto && (
                          <FiAlertTriangle className="text-red-500 mt-1 flex-shrink-0" size={16} title="Registro devuelto - Requiere atención" />
                        )}
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <div className="text-sm font-medium text-gray-900">
                              {dato.tema}
                            </div>
                            {dato.fueDevuelto && (
                              <span className="px-1.5 py-0.5 bg-red-100 text-red-800 text-xs font-semibold rounded">
                                DEVUELTO
                              </span>
                            )}
                          </div>
                          <div className="text-sm text-gray-500 truncate max-w-md">
                            {dato.descripcion}
                          </div>
                          {dato.fueDevuelto && dato.ultimaTransicion?.motivoTransicion && (
                            <div className="mt-1 text-xs text-red-700">
                              <strong>Motivo:</strong> {dato.ultimaTransicion.motivoTransicion.length > 100 
                                ? dato.ultimaTransicion.motivoTransicion.substring(0, 100) + '...' 
                                : dato.ultimaTransicion.motivoTransicion}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getEstadoColor(dato.estado)}`}>
                        {getEstadoLabel(dato.estado)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getPrioridadColor(dato.prioridad)}`}>
                        {dato.prioridad}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-wrap gap-1">
                        {dato.catalogos?.slice(0, 2).map((cat) => (
                          <span
                            key={cat.id}
                            className="px-2 py-0.5 text-xs rounded"
                            style={{
                              backgroundColor: `${cat.color}20`,
                              color: cat.color
                            }}
                          >
                            {cat.nombre}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {dato.tiempoInvestigacionHoras || 0}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex justify-end space-x-3">
                        <Link
                          to={`/historicidad/gestor/editar/${dato.id}`}
                          className="text-blue-600 hover:text-blue-900 p-1"
                          title="Editar"
                        >
                          <FiEdit2 size={18} />
                        </Link>
                        {dato.estado === 'creada_gestor' && (
                          <button
                            onClick={() => abrirModalEnvio(dato)}
                            className="text-green-600 hover:text-green-900 p-1 flex items-center"
                            title="Enviar a validación"
                          >
                            <FiSend size={18} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          </>
        )}
        </>
        )}
      </div>

      {/* Modal de confirmación de envío */}
      {selectedRole === 'gestor' && (
        <ModalEnviarFlujo
          isOpen={modalOpen}
          onClose={cerrarModal}
          onConfirm={confirmarEnvio}
          datoTema={datoSeleccionado?.tema || ''}
          estadoActual={datoSeleccionado?.estado || 'creada_gestor'}
          siguienteRol="Validador"
          modoMasivo={modoMasivo}
          cantidadSeleccionados={seleccionados.length}
        />
      )}
    </div>
  );
};

export default BandejaGestor;

